"use client";

import { createContext, useContext, ReactNode, useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Code, Search, Eye, Shield, Hammer } from "lucide-react";
import { INSIGHT_TYPES, INSIGHT_LANGUAGE } from "@/bryntum-lib/models/Insights";
import { getAdvisorTagline } from "../advisorContent";
import { useFileInsights } from "@/react/hooks/entityHooks/useFileInsights";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { Insights } from "@/bryntum-lib/models/Insights";
import { deleteInspectionInsight, getUnassignedFileInsights } from "@/network/network";
import { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import { EntityRecord } from "@/types/entity";

export interface InsightMessage {
  id: string;
  personaId: string;
  personaName: string;
  content: string;
  timestamp: Date;
}

export interface Persona {
  id: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  avatarUrl?: string | null;
}

// Map insight types to icons
const INSIGHT_TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  analyze: Search,
  execute: Code,
  review: Eye,
  site_safety: Shield,
  workmanship: Hammer
};

export type GeneratingUiMode = "expanded" | "compact";

export type GenerationSuccessNotice = {
  personaId: string;
  personaName: string;
  at: number;
};

interface AnalyzeFileContextValue {
  // State
  messages: InsightMessage[];
  typingPersonas: Set<string>;
  generatingUiModes: Record<string, GeneratingUiMode>;
  personas: Persona[];
  isLoadingAvatars: boolean;
  /** Feed filter (null = All). Lives in provider so dock ↔ pop-out does not reset selection. */
  feedFilter: string | null;
  setFeedFilter: (personaId: string | null) => void;
  /** Set when an advisor run finishes successfully; cleared after UI acknowledges. */
  generationSuccessNotice: GenerationSuccessNotice | null;
  acknowledgeGenerationSuccess: () => void;
  lastInsightError: string | null;
  clearInsightError: () => void;

  // Actions
  handlePersonaSelect: (persona: Persona) => void;
  handleDeleteMessage: (id: string) => void;
  resetState: () => void;
  getGenerationStartedAt: (personaId: string) => number | undefined;
}

const AnalyzeFileContext = createContext<AnalyzeFileContextValue | undefined>(undefined);

interface AnalyzeFileProviderProps {
  children: ReactNode;
  open: boolean;
  file: SwivuFile;
  entityRecord?: EntityRecord;
  unassignedFileId?: number;
  insightBoardId?: number;
}

export const AnalyzeFileProvider = ({ children, open, file: propFile, unassignedFileId, insightBoardId }: AnalyzeFileProviderProps) => {
  const isUnassignedMode = unassignedFileId != null;
  const [localFile, setLocalFile] = useState<SwivuFile>(propFile);
  const fileId = localFile.id;
  const [messages, setMessages] = useState<InsightMessage[]>([]);
  const [typingPersonas, setTypingPersonas] = useState<Set<string>>(new Set());
  const [personaAvatars, setPersonaAvatars] = useState<Record<string, string | null>>({});
  const [isLoadingAvatars, setIsLoadingAvatars] = useState<boolean>(true);
  const activeRequestsRef = useRef<Map<string, AbortController>>(new Map());
  const activeRequestParamsRef = useRef<Map<string, { file_id: number; insight_type: string; company_cam_url?: string; unassigned_file_id?: number }>>(new Map());
  const generationStartedAtRef = useRef<Map<string, number>>(new Map());
  const [generatingUiModes, setGeneratingUiModes] = useState<Record<string, GeneratingUiMode>>({});
  const [feedFilter, setFeedFilter] = useState<string | null>(null);
  const [generationSuccessNotice, setGenerationSuccessNotice] = useState<GenerationSuccessNotice | null>(null);
  const acknowledgeGenerationSuccess = useCallback(() => setGenerationSuccessNotice(null), []);
  const { getInsights, generateInsight, deleteInsight, getInsightTypesAvatar, cancelInsight, generateInsightForUnassignedFile, cancelInsightForUnassignedFile, reportInsightError, lastError: lastInsightError, clearError: clearInsightError } = useFileInsights();

  // Subscribe to store changes for reactive updates
  const { version } = useSwivuStore({ storeId: STORE_KEYS.insightsStore });

  // Create personas from INSIGHT_TYPES
  const personas = useMemo<Persona[]>(() => {
    return Object.values(INSIGHT_TYPES).map((insightType) => ({
      id: insightType.value,
      name: insightType.label,
      icon: INSIGHT_TYPE_ICONS[insightType.value] || Code,
      description: getAdvisorTagline(insightType.value),
      avatarUrl: personaAvatars[insightType.value]
    }));
  }, [personaAvatars]);

  useEffect(() => {
    if (propFile) {
      setLocalFile(propFile);
    }
  }, [propFile]);

  // Fetch avatars for all insight types when component mounts
  useEffect(() => {
    if (!open) {
      setIsLoadingAvatars(false);
      return;
    }

    const fetchAvatars = async () => {
      setIsLoadingAvatars(true);
      try {
        const avatarPromises = Object.values(INSIGHT_TYPES).map(async (insightType) => {
          const avatarUrl = await getInsightTypesAvatar(insightType.value);
          return { type: insightType.value, url: avatarUrl };
        });

        const results = await Promise.all(avatarPromises);
        const avatarMap: Record<string, string | null> = {};
        results.forEach(({ type, url }) => {
          avatarMap[type] = url;
        });
        setPersonaAvatars(avatarMap);
      } catch (error) {
        console.error("[AnalyzeFileContext] Failed to fetch avatars:", error);
      } finally {
        setIsLoadingAvatars(false);
      }
    };

    fetchAvatars();
  }, [open, getInsightTypesAvatar]);

  // Load existing insights for unassigned files from API
  const loadUnassignedInsights = useCallback(async () => {
    if (!unassignedFileId) return;
    try {
      const response = await getUnassignedFileInsights(unassignedFileId);
      const insightsData = response.data || [];
      const insightMessages: InsightMessage[] = insightsData.map((insight: Record<string, unknown>) => ({
        id: String(insight.id),
        personaId: insight.insight_type as string,
        personaName: INSIGHT_TYPES[insight.insight_type as keyof typeof INSIGHT_TYPES]?.label || (insight.insight_type as string),
        content: (insight.content as string) || "",
        timestamp: insight.created_at ? new Date(insight.created_at as string) : new Date(),
      }));
      insightMessages.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

      setTypingPersonas((prev) => {
        const newSet = new Set(prev);
        insightMessages.forEach((msg) => newSet.delete(msg.personaId));
        return newSet;
      });

      setMessages(insightMessages);
    } catch (error) {
      reportInsightError({
        error,
        provider: "openai",
        fallback: "Failed to load insights.",
      });
    }
  }, [unassignedFileId, reportInsightError]);

  // Load existing insights when dialog opens, fileId changes, or store updates
  useEffect(() => {
    if (!open) {
      setMessages([]);
      return;
    }

    if (isUnassignedMode) {
      loadUnassignedInsights();
      return;
    }

    if (!fileId) {
      setMessages([]);
      return;
    }

    const insights = getInsights(Number(fileId));
    const insightMessages: InsightMessage[] = insights.map((insight: Insights) => ({
      id: String(insight.id),
      personaId: insight.insightType,
      personaName: INSIGHT_TYPES[insight.insightType as keyof typeof INSIGHT_TYPES]?.label || insight.insightType,
      content: insight.content || "",
      timestamp: insight.createdAt ? new Date(insight.createdAt) : new Date()
    }));

    insightMessages.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    setTypingPersonas((prev) => {
      const newSet = new Set(prev);
      insightMessages.forEach((msg) => {
        // Keep typing indicator while a generation request is still in flight
        if (!activeRequestsRef.current.has(msg.personaId)) {
          newSet.delete(msg.personaId);
        }
      });
      return newSet;
    });

    setMessages(insightMessages);
  }, [open, fileId, localFile, version, getInsights, isUnassignedMode, loadUnassignedInsights]);

  const handlePersonaSelect = useCallback(
    async (persona: Persona) => {
      if (activeRequestsRef.current.has(persona.id)) {
        console.warn(`[AnalyzeFileContext] Insight generation already in progress for "${persona.name}"`);
        return;
      }

      if (isUnassignedMode && (!unassignedFileId || !insightBoardId)) {
        return;
      }

      const abortController = new AbortController();
      activeRequestsRef.current.set(persona.id, abortController);

      const hadPriorInsight = messages.some((m) => m.personaId === persona.id);
      generationStartedAtRef.current.set(persona.id, Date.now());
      setGeneratingUiModes((prev) => ({
        ...prev,
        [persona.id]: hadPriorInsight ? "compact" : "expanded",
      }));

      setGenerationSuccessNotice(null);
      clearInsightError();
      setTypingPersonas((prev) => new Set(prev).add(persona.id));

      try {
        if (isUnassignedMode && unassignedFileId && insightBoardId) {
          activeRequestParamsRef.current.set(persona.id, {
            file_id: 0,
            insight_type: persona.id,
            unassigned_file_id: unassignedFileId,
          });

          await generateInsightForUnassignedFile({
            unassigned_file_id: unassignedFileId,
            insight_type: persona.id,
            language: INSIGHT_LANGUAGE.english.value,
            signal: abortController.signal,
            board_id: insightBoardId,
          });
          await loadUnassignedInsights();
          setGenerationSuccessNotice({
            personaId: persona.id,
            personaName: persona.name,
            at: Date.now(),
          });
        } else {
          if (!localFile.id) return;

          activeRequestParamsRef.current.set(persona.id, {
            file_id: Number(localFile.id),
            insight_type: persona.id,
          });

          await generateInsight(
            Number(localFile.id),
            persona.id,
            INSIGHT_LANGUAGE.english.value,
            abortController.signal
          );
          setGenerationSuccessNotice({
            personaId: persona.id,
            personaName: persona.name,
            at: Date.now(),
          });
        }
      } catch (error: any) {
        if (error?.name !== 'AbortError' && error?.message !== 'canceled') {
          console.error("[AnalyzeFileContext] Failed to generate insight:", error);
        }
        if (error?.name !== 'AbortError' && error?.message !== 'canceled') {
          setTypingPersonas((prev) => {
            const newSet = new Set(prev);
            newSet.delete(persona.id);
            return newSet;
          });
          generationStartedAtRef.current.delete(persona.id);
          setGeneratingUiModes((prev) => {
            const { [persona.id]: _removed, ...rest } = prev;
            return rest;
          });
        }
      } finally {
        activeRequestsRef.current.delete(persona.id);
        activeRequestParamsRef.current.delete(persona.id);
        generationStartedAtRef.current.delete(persona.id);
        setTypingPersonas((prev) => {
          const next = new Set(prev);
          next.delete(persona.id);
          return next;
        });
        setGeneratingUiModes((prev) => {
          const { [persona.id]: _removed, ...rest } = prev;
          return rest;
        });
      }
    },
    [generateInsight, generateInsightForUnassignedFile, localFile, isUnassignedMode, unassignedFileId, insightBoardId, loadUnassignedInsights, messages]
  );

  const getGenerationStartedAt = useCallback((personaId: string) => {
    return generationStartedAtRef.current.get(personaId);
  }, []);

  const handleDeleteMessage = useCallback(
    (id: string) => {
      if (isUnassignedMode) {
        const numericId = Number(id);
        if (!Number.isFinite(numericId)) {
          console.error("[AnalyzeFileContext] Invalid insight id for delete:", id);
          return;
        }
        void (async () => {
          try {
            await deleteInspectionInsight(numericId);
            setMessages((prev) => prev.filter((m) => m.id !== id));
          } catch (error) {
            console.error("[AnalyzeFileContext] Failed to delete unassigned insight:", error);
          }
        })();
        return;
      }
      deleteInsight(id);
    },
    [deleteInsight, isUnassignedMode]
  );

  const resetState = useCallback(() => {
    setMessages([]);
    setTypingPersonas(new Set());
    setGeneratingUiModes({});
    generationStartedAtRef.current.clear();
    setFeedFilter(null);
    setGenerationSuccessNotice(null);
    setIsLoadingAvatars(true);
  }, []);

  // Cancel all active requests and reset state when dialog closes
  useEffect(() => {
    if (!open) {
      // Cancel all active requests
      activeRequestsRef.current.forEach((controller, personaId) => {
        console.log(`[AnalyzeFileContext] Canceling pending request for persona: ${personaId}`);
        controller.abort();

        // Notify backend about cancellation
        const params = activeRequestParamsRef.current.get(personaId);
        if (params) {
          if (params.unassigned_file_id != null) {
            void cancelInsightForUnassignedFile(params.unassigned_file_id, params.insight_type);
          } else {
            void cancelInsight(params.file_id, params.insight_type, params.company_cam_url);
          }
        }
      });
      activeRequestsRef.current.clear();
      activeRequestParamsRef.current.clear();

      // Small delay to allow animations to complete
      setTimeout(() => {
        resetState();
      }, 300);
    }
  }, [open, resetState, cancelInsight, cancelInsightForUnassignedFile]);

  const cancelInsightRef = useRef(cancelInsight);
  const cancelUnassignedRef = useRef(cancelInsightForUnassignedFile);
  cancelInsightRef.current = cancelInsight;
  cancelUnassignedRef.current = cancelInsightForUnassignedFile;

  // Provider unmount while requests still active (e.g. My Media closed with dialog open)
  useEffect(() => {
    return () => {
      activeRequestsRef.current.forEach((controller, personaId) => {
        controller.abort();
        const params = activeRequestParamsRef.current.get(personaId);
        if (params) {
          if (params.unassigned_file_id != null) {
            void cancelUnassignedRef.current(params.unassigned_file_id, params.insight_type);
          } else {
            void cancelInsightRef.current(params.file_id, params.insight_type, params.company_cam_url);
          }
        }
      });
      activeRequestsRef.current.clear();
      activeRequestParamsRef.current.clear();
    };
  }, []);

  const value: AnalyzeFileContextValue = {
    messages,
    typingPersonas,
    generatingUiModes,
    personas,
    isLoadingAvatars,
    feedFilter,
    setFeedFilter,
    generationSuccessNotice,
    acknowledgeGenerationSuccess,
    lastInsightError,
    clearInsightError,
    handlePersonaSelect,
    handleDeleteMessage,
    resetState,
    getGenerationStartedAt,
  };

  return <AnalyzeFileContext.Provider value={value}>{children}</AnalyzeFileContext.Provider>;
};

export const useAnalyzeFileContext = () => {
  const context = useContext(AnalyzeFileContext);
  if (context === undefined) {
    throw new Error("useAnalyzeFileContext must be used within an AnalyzeFileProvider");
  }
  return context;
};
